<template>
  <div class="fit-viewport">
    <div class="pad" ref="pad">
      <router-view></router-view>
    </div>
  </div>
</template>


<script>

// theme-a：2048×1280 设计画布 + transform 拉伸铺满适配任意视口
// （x/y 独立缩放保证全屏无黑边；16:10 设备 sx≈sy 无形变。
//   等比 min 缩放在视口比例≠1.6 时（如壳未隐藏状态栏）会两侧露边，故弃用）
const DESIGN_W = 2048;
const DESIGN_H = 1280;

// 软键盘弹出会把 window 高度压小（WebView adjustResize），若随之重算整页会被压缩——
// 高度变矮时保持原变换不重算；宽度变化（旋转）或高度恢复才重算并刷新基准
let baseW = 0;
let baseH = 0;

function fitPad() {
  const pad = document.querySelector('.pad');
  if (!pad) return;
  const w = window.innerWidth;
  const h = window.innerHeight;
  if (baseH && w === baseW && h < baseH) return;
  baseW = w;
  baseH = h;
  const sx = w / DESIGN_W;
  const sy = h / DESIGN_H;
  pad.style.transform = `scale(${sx}, ${sy})`;
}

export default {
  name: 'App',
  mounted() {
    fitPad();
    window.addEventListener('resize', fitPad);
    this._fitPad = fitPad;
  },
  beforeDestroy() {
    window.removeEventListener('resize', this._fitPad || fitPad);
  },
}

</script>


<style lang="less">
// 全局共享骨架样式（选择页/表单页/签到页共用的 SuperPage 体系）
// 设计令牌见 src/styles/design.less（theme-a：2048×1280 画布）
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
  -webkit-tap-highlight-color: transparent;
}

html, body {
  height: 100%;
  background: #23262b; // 画布外留边（比例不一致时）
  overflow: hidden;
}

// ---------- theme-a 固定画布 ----------
.fit-viewport {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.pad {
  width: 2048px;
  height: 1280px;
  flex: none;
  background: var(--bg);
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  transform-origin: center center;
  color: var(--ink);
  font-family: "PingFang SC", "HarmonyOS Sans SC", "Microsoft YaHei", -apple-system, "Helvetica Neue", Arial, sans-serif;
}

// ---------- theme-a · 返回按钮（圆形白底） ----------
.back-btn {
  position: absolute;
  top: 88px;
  right: 72px;
  z-index: 20;
  width: 104px;
  height: 104px;
  border-radius: 50%;
  background: var(--panel);
  border: 1px solid var(--line);
  box-shadow: var(--shadow-card);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  img, svg {
    width: 46px;
    height: 46px;
    display: block;
  }
}

// ---------- theme-a · 步骤条 ----------
.stepper {
  flex: none;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 56px 240px 0;
  position: relative;

  .track {
    position: absolute;
    top: 88px;
    left: 420px;
    right: 420px;
    height: 6px;
    background: #e3e6e2;
    border-radius: 3px;

    .fill {
      height: 100%;
      background: var(--blue);
      border-radius: 3px;
      transition: width 0.4s;
    }
  }

  .step {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 340px;
    position: relative;
    z-index: 1;

    .dot {
      width: 64px;
      height: 64px;
      border-radius: 50%;
      background: var(--panel);
      border: 4px solid #e3e6e2;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 28px;
      font-weight: 600;
      color: var(--ink-3);
    }

    .label {
      margin-top: 22px;
      font-size: 32px;
      color: var(--ink-3);
      font-weight: 500;
    }

    &.active .dot {
      border-color: var(--blue);
      color: var(--blue);
    }
    &.active .label {
      color: var(--ink);
      font-weight: 600;
    }
    &.done .dot {
      background: var(--blue);
      border-color: var(--blue);
      color: #fff;
    }
    &.done .label {
      color: var(--blue);
    }
  }
}

// ---------- theme-a · 功能大卡片（服务入口） ----------
.svc {
  background: var(--panel);
  border: 1px solid var(--line);
  border-radius: 32px;
  box-shadow: var(--shadow-card);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 48px;
  cursor: pointer;
  transition: transform 0.15s;

  &:active {
    transform: scale(0.97);
  }

  .ico, img {
    width: 120px;
    height: 120px;
    object-fit: contain;
    display: block;
  }

  .name {
    font-size: 48px;
    font-weight: 600;
    letter-spacing: 0.02em;
    color: var(--ink);
  }
}

// ---------- theme-a · 数字键盘 ----------
.keypad {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  background: #eceeec;
  padding: 20px;
  border-radius: 24px;

  .key {
    height: 150px;
    border-radius: 18px;
    border: none;
    background: #fff;
    font-size: 52px;
    font-weight: 500;
    font-family: inherit;
    color: var(--ink);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    box-shadow: 0 2px 6px rgba(20, 30, 25, 0.05);

    &:active {
      background: #eef1ee;
    }

    &.span2 {
      grid-column: span 2;
    }
  }
}

// ---------- 选择页（登记/签到/签离方式选择）----------
.select-page-box {
  background: var(--page-bg);
  height: 100%;
  box-sizing: border-box;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;

  .main-header {
    box-sizing: border-box;
    width: 100%;
    margin-top: 20px;
    height: 60px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    color: var(--text-1);
    padding: 20px 40px;
    font-size: 26px;
  }

  .svc-list {
    flex: 1;
    display: flex;
    gap: 72px;
    align-items: center;
    justify-content: center;
    width: 100%;
    padding-bottom: 120px;
    box-sizing: border-box;
  }

  .main-content {
    width: 100%;
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;

    .title {
      display: flex;
      justify-content: center;
      align-items: center;
      font-size: 72px;
      font-weight: 700;
      color: var(--text-1);
      letter-spacing: 2px;
      margin-bottom: 100px;
    }

    .my-card-list {
      display: flex;
      flex-wrap: nowrap;
      flex-direction: row;
      justify-content: center;
      align-items: center;
      gap: 72px;
      width: 100%;

      .el-card-box {
        .el-card-small {
          text-align: center;
          background: var(--panel);
          border: 1px solid var(--line);
          border-radius: 32px;
          box-shadow: var(--shadow-card);
          width: 500px;
          height: 520px;
          cursor: pointer;
          transition: transform 0.15s ease;
          overflow: hidden;

          &:active {
            transform: scale(0.97);
          }

          .el-card__body {
            height: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 0;
          }

          .el-card-content {
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            gap: 48px;
            font-weight: 600;
            font-size: 48px;
            letter-spacing: 0.02em;
            color: var(--ink);
            width: 100%;

            img {
              width: 120px;
              height: 120px;
              object-fit: contain;
              margin-bottom: 0;
            }
          }
        }
      }
    }
  }
}

// ---------- 通用页（表单/流程页）----------
.common-page-box {
  height: 100%;
  background: var(--page-bg);
  display: flex;
  flex-direction: column;

  .header-box {
    height: 80px !important;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    color: var(--text-1);
    font-size: 26px;

    .title {
      font-weight: bold;
      font-size: 40px;
    }
  }

  .el-footer {
    height: 60px;
    line-height: 60px;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .el-main {
    height: calc(100% - 140px);
    margin: 0 96px 40px;
    border-radius: var(--radius-card);
    background: var(--panel);
    border: 1px solid var(--line);
    box-shadow: var(--shadow-card);
  }

  .footer-button {
    width: 320px;
  }

  .main-content {
    display: flex;
    flex-direction: column;
    justify-content: start;
    align-items: center;
    overflow: auto;
    scrollbar-width: none;
    -ms-overflow-style: none;

    ::-webkit-scrollbar {
      display: none;
    }

    .search-box {
      display: flex;
      flex-direction: row;
      justify-content: flex-start;
      align-items: center;
      gap: 24px;
      margin: 40px 40px 0;
      width: 100%;

      .sub-title {
        font-size: 40px;
        font-weight: 600;
        white-space: nowrap;
        color: var(--ink);
        margin-right: 10px;
      }

      .el-input {
        flex: 1;
      }

      .el-button {
        height: 88px;
        padding: 0 72px;
        font-size: 34px;
        white-space: nowrap;
        flex: none;
      }
    }

    .agreement-box {
      display: flex;
      justify-content: flex-start;
      align-items: center;
      margin: 36px 40px;
      width: 100%;
      padding-left: 176px;
      box-sizing: border-box;

      span {
        color: var(--blue);
        cursor: pointer;
        font-size: 32px;
      }
    }

    .main-half {
      display: flex;
      padding-top: 24px;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      width: 100%;

      .agreement-title {
        width: 90%;
        font-size: 36px;
        margin-top: 40px;
        font-weight: 700;
        color: var(--ink);
      }

      .agreement-text {
        justify-content: start;
        align-items: center;
        width: 90%;
        font-size: 32px;
        line-height: 1.9;
        color: var(--text-2);
      }
    }
  }
}

.empty {
  width: 1px;
  height: 1px;
}

.header-sign {
  height: 60px;
  line-height: 60px;
  padding: 10px;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;

  .title {
    margin-left: 20px;
    font-weight: bold;
    font-size: 36px;
    color: var(--text-1);
  }

  .back-icon {
    margin-top: 10px;
  }
}
</style>
